import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { isValidReturnToUrl, resolveReturnToRedirect } from '@/utils/returnToValidation';
import { DOMAIN_CONFIG } from '@/integrations/supabase/config';

export const useLoginForm = (setLoading: (loading: boolean) => void, returnTo?: string | null) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const navigate = useNavigate();

  const resetError = () => {
    setError('');
    setVerificationSent(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validateLoginInputs = () => {
    resetError();
    
    if (!email || !password) {
      setError('Please fill in all the required fields.');
      return false;
    }
    
    return true;
  };

  const validatePasswordUpdate = () => {
    resetError();
    
    if (!password) {
      setError('Please enter a new password.');
      return false;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords don\'t match.');
      return false;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return false;
    }
    
    return true;
  };

  const resendVerificationEmail = async () => {
    if (!email) {
      setError('Please enter your email address to resend verification.');
      return;
    }

    try {
      setLoading(true);
      
      // Get current origin for proper redirects
      const currentOrigin = window.location.origin;
      
      // Construct the redirect URL
      const redirectUrl = `${currentOrigin}/auth?tab=login`;
      
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: redirectUrl,
        }
      });
      console.log('error', error);
      
      if (error) throw error;
      
      setVerificationSent(true);
      toast.success("Verification email sent. Please check your inbox and click the verification link.");
    } catch (error: any) {
      console.error('Error resending verification:', error);
      setError(error.message || 'Failed to resend verification email.');
      toast.error(error.message || 'Failed to resend verification email.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateLoginInputs()) {
      return;
    }
    
    try {
      setLoading(true);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        throw error;
      }
      
      toast.success(`Welcome${data.user?.user_metadata?.name ? ` ${data.user.user_metadata.name}` : ''}!`);
      localStorage.setItem('userSessionId', data.user?.id || '');

      // Check user role to determine redirect behavior
      const { data: profile } = await supabase
        .from('profiles')
        .select('is_recruiter, is_admin, is_superadmin, onboarding_status')
        .eq('id', data.user.id)
        .single();

      const isPrivilegedRole = profile?.is_recruiter || profile?.is_admin || profile?.is_superadmin;

      // Skip onboarding for privileged roles
      if (isPrivilegedRole && profile?.onboarding_status !== 'completed') {
        await supabase
          .from('profiles')
          .update({ onboarding_status: 'completed', onboarding_step: 5 })
          .eq('id', data.user.id);
      }

      // Determine redirect destination
      if (returnTo && isValidReturnToUrl(returnTo)) {
        const { url, isExternal } = resolveReturnToRedirect(returnTo);
        if (isExternal) {
          window.location.href = url;
        } else {
          navigate(url);
        }
      } else if (isPrivilegedRole) {
        if (profile?.is_recruiter) {
          window.location.href = `https://${DOMAIN_CONFIG.recruiterDomain}/search`;
        } else if (profile?.is_superadmin) {
          navigate('/superadmin');
        } else {
          navigate('/dashboard');
        }
      } else {
        navigate('/dashboard');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Handle email verification errors
      if (error.message && error.message.includes('Email not confirmed')) {
        setError('Please verify your email before logging in. Check your inbox for the verification link.');
        setVerificationSent(true);
        toast.error("Please check your email and click the verification link we sent you.");
        return;
      }
      
      // Handle database-related errors
      if (error.message && error.message.includes('Database error')) {
        setError('There was an issue with our database. Please try again later.');
        toast.error("We're experiencing technical issues. Please try again later.");
      } else {
        setError(error.message || 'Login failed. Please check your credentials.');
        toast.error(error.message || 'Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (onPasswordUpdate?: (password: string) => Promise<void>) => {
    if (!validatePasswordUpdate()) {
      return;
    }
    
    try {
      setLoading(true);
      
      if (!onPasswordUpdate) {
        throw new Error('Password update handler not provided');
      }

      await onPasswordUpdate(password);
      
      // Clear form after successful update
      setPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('Password update error:', error);
      setError(error.message || 'Failed to update password. Please try again.');
      toast.error(error.message || 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    showPassword,
    error,
    verificationSent,
    togglePasswordVisibility,
    resetError,
    handleLogin,
    handlePasswordUpdate,
    resendVerificationEmail
  };
};
